import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/SupabaseAuthContext';
// import { setupSystemUsers } from '../utils/systemUserSetup'; // Removed
import { apiService } from '../services/api';

interface SystemUser {
  id: string;
  name: string;
  email: string;
  system_role: 'super_admin' | 'manager' | 'viewer';
  is_active: boolean;
  phone?: string;
  last_login_at: string | null;
  created_at: string;
}

interface SystemRole {
  value: 'super_admin' | 'manager' | 'viewer';
  label: string;
  description: string;
  color: 'error' | 'primary' | 'secondary';
}

const systemRoles: SystemRole[] = [
  {
    value: 'super_admin',
    label: 'スーパー管理者',
    description: 'すべての権限を持つ最高権限者',
    color: 'error'
  },
  {
    value: 'manager',
    label: 'マネージャー',
    description: 'テナント・ユーザー管理権限',
    color: 'primary'
  },
  {
    value: 'viewer',
    label: 'ビューアー',
    description: '読み取り専用・レポート権限',
    color: 'secondary'
  }
];

function SystemUsers() {
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'super_admin' | 'manager' | 'viewer'>('manager');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [isSettingUp, setIsSettingUp] = useState(false);

  const { session, currentUser, isSystemAdmin, isSuperAdmin, loading: authLoading } = useAuth();

  useEffect(() => {
    loadSystemUsers();
  }, []);

  // 認証状態の変化を追跡
  useEffect(() => {
    console.log('🔐 Auth state changed:', {
      authLoading,
      hasSession: !!session,
      hasCurrentUser: !!currentUser,
      isSystemAdmin: isSystemAdmin()
    });
  }, [authLoading, session, currentUser, isSystemAdmin]);
  
  // デバッグログ：認証状態の変化を追跡
  console.log('Auth context state:', {
    hasSession: !!session,
    hasCurrentUser: !!currentUser,
    currentUserEmail: currentUser?.email,
    isSystemAdmin: isSystemAdmin(),
    isSuperAdmin: isSuperAdmin(),
    authLoading,
    shouldShowButton: !authLoading && !(session && !currentUser) && isSystemAdmin()
  });

  const loadSystemUsers = async () => {
    try {
      setLoading(true);
      
      const data = await apiService.getAllUsers() as SystemUser[];
      console.log('System users loaded:', data);
      console.log('User roles:', data.map((u: SystemUser) => ({ email: u.email, system_role: u.system_role })));
      
      // データが配列であることを確認
      if (Array.isArray(data)) {
        setUsers(data);
        console.log('System users loaded successfully:', data.length, 'users');
        
        // データの取得に成功した場合はエラーメッセージをクリア
        setMessage(null);
      } else {
        console.error('System users data is not an array:', data);
        setMessage({ type: 'error', text: 'システムユーザーデータの形式が正しくありません' });
      }
    } catch (error) {
      console.error('Failed to load system users:', error);
      setMessage({ type: 'error', text: 'システムユーザーの読み込みに失敗しました' });
      // エラー時は空の配列を設定（Mockデータは使用しない）
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const getRoleInfo = (role: string): SystemRole => {
    return systemRoles.find(r => r.value === role) || systemRoles[1];
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const response = await apiService.toggleUserStatus(userId);
      
      if (response.success && response.data) {
        // APIレスポンスからデータを更新
        setUsers(prev => prev.map(user => 
          user.id === userId ? {
            ...user, 
            is_active: response.data!.is_active,
            updated_at: response.data!.updated_at
          } : user
        ));
        
        setMessage({ 
          type: 'success', 
          text: response.message || `ユーザーを${response.data.is_active ? 'アクティブ' : '非アクティブ'}にしました` 
        });
      } else {
        throw new Error(response.message || 'API呼び出しが失敗しました');
      }
    } catch (error: any) {
      console.error('Failed to toggle user status:', error);
      
      // エラーメッセージを詳細に処理
      let errorMessage = 'ユーザーステータスの変更に失敗しました';
      
      if (error.message && error.message.includes('SYSTEM_ADMIN_REQUIRED')) {
        errorMessage = 'システムユーザーのステータスを変更するには、システム管理者権限が必要です。';
      } else if (error.message && error.message.includes('INSUFFICIENT_ROLE')) {
        errorMessage = 'システムユーザーのステータスを変更するには、manager または super_admin 権限が必要です。';
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '権限が不足しています。システム管理者に相談してください。';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleAddUser = async () => {
    try {
      await apiService.addSystemUser({
        email: newUserEmail, 
        role: newUserRole,
        name: newUserName || newUserEmail.split('@')[0], // 名前が入力されていない場合はメールアドレスから生成
        password: newUserPassword
      });
      
      setMessage({ type: 'success', text: 'システムユーザーを追加しました。' });
      setAddDialogOpen(false);
      setNewUserEmail('');
      setNewUserName('');
      setNewUserPassword('');
      setNewUserRole('manager');
      loadSystemUsers();
    } catch (error: any) {
      console.error('Failed to add user:', error);
      
      // エラーメッセージを詳細に処理
      let errorMessage = 'システムユーザーの追加に失敗しました';
      
      if (error.message && error.message.includes('SYSTEM_ADMIN_REQUIRED')) {
        errorMessage = 'システムユーザーを追加するには、システム管理者権限が必要です。システム管理者に権限の付与を依頼してください。';
      } else if (error.message && error.message.includes('NO_SYSTEM_ROLE')) {
        errorMessage = 'システム管理者権限が見つかりません。データベース設定を確認してください。';
      } else if (error.message && error.message.includes('INSUFFICIENT_ROLE')) {
        errorMessage = 'システムユーザーを追加するには、manager または super_admin 権限が必要です。';
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '権限が不足しています。システム管理者に相談してください。';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await apiService.updateSystemUser(selectedUser.id, {
        name: selectedUser.name,
        system_role: selectedUser.system_role,
        phone: selectedUser.phone,
        is_active: selectedUser.is_active
      });
      
      if (response.success && response.data) {
        // APIレスポンスからデータを更新
        setUsers(prev => prev.map(user => 
          user.id === selectedUser.id ? {
            ...user,
            name: response.data!.name,
            system_role: response.data!.system_role as 'super_admin' | 'manager' | 'viewer',
            phone: response.data!.phone,
            is_active: response.data!.is_active,
            updated_at: response.data!.updated_at
          } : user
        ));
        
        setMessage({ type: 'success', text: response.message || 'ユーザー情報を更新しました' });
      } else {
        throw new Error(response.message || 'API呼び出しが失敗しました');
      }
      
      setEditDialogOpen(false);
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Failed to update user:', error);
      
      // エラーメッセージを詳細に処理
      let errorMessage = 'ユーザー情報の更新に失敗しました';
      
      if (error.message && error.message.includes('SYSTEM_ADMIN_REQUIRED')) {
        errorMessage = 'システムユーザーを編集するには、システム管理者権限が必要です。';
      } else if (error.message && error.message.includes('INSUFFICIENT_ROLE')) {
        errorMessage = 'システムユーザーを編集するには、manager または super_admin 権限が必要です。';
      } else if (error.message && error.message.includes('403')) {
        errorMessage = '権限が不足しています。システム管理者に相談してください。';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleSetupSystemUsers = async () => {
    setIsSettingUp(true);
    try {
      setMessage({ type: 'success', text: 'システムユーザーは既にSupabaseで管理されています' });
      
      // リストをリロード
      await loadSystemUsers();
    } catch (error) {
      console.error('Setup error:', error);
      setMessage({ type: 'error', text: 'システムユーザーのリロードに失敗しました' });
    } finally {
      setIsSettingUp(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: '名前', width: 150 },
    { field: 'email', headerName: 'メールアドレス', width: 250 },
    {
      field: 'system_role',
      headerName: 'システムロール',
      width: 150,
      renderCell: (params) => {
        const roleInfo = getRoleInfo(params.value);
        return (
          <Chip
            label={roleInfo.label}
            size="small"
            color={roleInfo.color}
          />
        );
      },
    },
    {
      field: 'is_active',
      headerName: 'ステータス',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'アクティブ' : '非アクティブ'}
          size="small"
          color={params.value ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'last_login_at',
      headerName: '最終ログイン',
      width: 150,
      valueFormatter: (params) =>
        params.value
          ? new Date(params.value).toLocaleDateString('ja-JP')
          : '未ログイン',
    },
    {
      field: 'created_at',
      headerName: '作成日',
      width: 150,
      valueFormatter: (params) =>
        new Date(params.value).toLocaleDateString('ja-JP'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'アクション',
      width: 120,
      getActions: (params) => {
        // 認証ロード中または権限がない場合はアクションを表示しない
        if (authLoading || (session && !currentUser) || !isSystemAdmin()) {
          return [];
        }
        
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="編集"
            onClick={() => {
              setSelectedUser(params.row);
              setEditDialogOpen(true);
            }}
          />,
          <GridActionsCellItem
            icon={params.row.is_active ? <BlockIcon /> : <CheckCircleIcon />}
            label={params.row.is_active ? '無効化' : '有効化'}
            onClick={() => handleToggleUserStatus(params.row.id, params.row.is_active)}
          />,
        ];
      },
    },
  ];

  const filteredUsers = users.filter(
    (user) =>
      (user.name?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
      (user.email?.toLowerCase() || '').includes(searchText.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            システムユーザー管理
          </Typography>
          <Typography variant="body1" color="textSecondary">
            システム全体を管理するユーザーの権限管理を行います
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* 開発環境またはユーザーが少ない場合のみ表示 */}
          {!authLoading && (process.env.NODE_ENV === 'development' || users.length < 3) && isSystemAdmin() && (
            <Button
              variant="outlined"
              onClick={handleSetupSystemUsers}
              disabled={isSettingUp}
            >
              {isSettingUp ? '設定中...' : '初期ユーザー設定'}
            </Button>
          )}
          {/* システム管理者のみに追加ボタンを表示 - 認証ロード中はプレースホルダーを表示 */}
          {authLoading || (session && !currentUser) ? (
            <Button
              variant="contained"
              disabled
              startIcon={<AddIcon />}
            >
              読み込み中...
            </Button>
          ) : isSystemAdmin() ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              システムユーザー追加
            </Button>
          ) : null}
        </Box>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}


      {/* 権限説明カード */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          システムロール権限
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {systemRoles.map((role) => (
            <Box key={role.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={role.label}
                size="small"
                color={role.color}
              />
              <Typography variant="body2" color="textSecondary">
                {role.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="名前、メールアドレスで検索"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Paper>

      {/* ユーザー追加ダイアログ */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>システムユーザー追加</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="名前"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="例: 田中 太郎"
            />
            <TextField
              fullWidth
              label="メールアドレス"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="パスワード"
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              sx={{ mb: 2 }}
              required
              helperText="8文字以上で設定してください"
            />
            <FormControl fullWidth>
              <InputLabel>システムロール</InputLabel>
              <Select
                value={newUserRole}
                label="システムロール"
                onChange={(e) => setNewUserRole(e.target.value as any)}
              >
                {systemRoles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={role.label} size="small" color={role.color} />
                      <Typography variant="body2">{role.description}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setNewUserEmail('');
            setNewUserName('');
            setNewUserPassword('');
            setNewUserRole('manager');
          }}>
            キャンセル
          </Button>
          <Button 
            onClick={handleAddUser}
            variant="contained"
            disabled={!newUserEmail || !newUserPassword || newUserPassword.length < 8}
          >
            追加
          </Button>
        </DialogActions>
      </Dialog>

      {/* ユーザー編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>システムユーザー編集</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="名前"
                value={selectedUser.name}
                onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="メールアドレス"
                value={selectedUser.email}
                onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                sx={{ mb: 2 }}
                disabled
              />
              <TextField
                fullWidth
                label="電話番号（オプション）"
                value={selectedUser.phone || ''}
                onChange={(e) => setSelectedUser({ ...selectedUser, phone: e.target.value })}
                sx={{ mb: 2 }}
                placeholder="例: 090-1234-5678"
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>システムロール</InputLabel>
                <Select
                  value={selectedUser.system_role}
                  label="システムロール"
                  onChange={(e) => setSelectedUser({ ...selectedUser, system_role: e.target.value as any })}
                >
                  {systemRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={role.label} size="small" color={role.color} />
                        <Typography variant="body2">{role.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleEditUser}
            variant="contained"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* デバッグ情報：開発環境でのみ表示（ページ下部） */}
      {process.env.NODE_ENV === 'development' && (
        <Paper sx={{ p: 2, mt: 4, bgcolor: '#f5f5f5', border: '1px dashed #ccc' }}>
          <Typography variant="subtitle2" gutterBottom>
            デバッグ情報（開発環境のみ）
          </Typography>
          <Typography variant="body2" component="pre" sx={{ fontSize: '11px', overflow: 'auto' }}>
            {JSON.stringify({
              authLoading,
              hasSession: !!session,
              hasCurrentUser: !!currentUser,
              currentUserEmail: currentUser?.email,
              isSystemAdmin: isSystemAdmin(),
              isSuperAdmin: isSuperAdmin(),
              currentUserPermissions: currentUser?.permissions || [],
              currentUserRoles: currentUser?.roles || []
            }, null, 2)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default SystemUsers;